import { Outlet, createFileRoute, redirect, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { isLoggedIn } from '@/api';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { AccountSidebar, AccountTipsCard } from '@/ui/profile';

/** Layout route: account sidebar (desktop) + child screens. Requires login. */
export const Route = createFileRoute('/profile')({
  beforeLoad: ({ location }) => {
    if (!isLoggedIn()) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }
  },
  component: ProfileLayout,
});

function ProfileLayout() {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  const isDetails = pathname === '/profile/details' || pathname === '/profile/details/';
  const isTransactions =
    pathname === '/profile/transactions' || pathname === '/profile/transactions/';
  const isTickets = pathname === '/profile/tickets' || pathname === '/profile/tickets/';
  const isBonuses = pathname === '/profile/bonuses' || pathname.startsWith('/profile/bonuses/');
  const isClaimBonus =
    pathname === '/profile/claimbonus' || pathname === '/profile/claimbonus/';
  const isMessagesList =
    pathname === '/profile/messages' || pathname === '/profile/messages/';
  const isMessageDetail = pathname.startsWith('/profile/messages/') && !isMessagesList;
  const isDeposits =
    pathname === '/profile/deposits' || pathname.startsWith('/profile/deposits/');
  const isWithdrawals =
    pathname === '/profile/withdrawals' || pathname.startsWith('/profile/withdrawals/');
  const tipTitle = isDetails
    ? label('Keep details current', 'Keep details current')
    : isTransactions
      ? label('Track your balance', 'Track your balance')
      : isTickets
        ? label('Review your tickets', 'Review your tickets')
        : isClaimBonus
          ? label('Claim eligibility', 'Claim eligibility')
          : isBonuses
            ? label('Finish wagering', 'Finish wagering')
            : isMessageDetail
              ? label('Stay informed', 'Stay informed')
              : isMessagesList
                ? label('Check your inbox', 'Check your inbox')
                : isDeposits
                  ? label('Deposit tips', 'Deposit tips')
                  : isWithdrawals
                    ? label('Withdrawal tips', 'Withdrawal tips')
                    : label('Account tips', 'Account tips');
  const tipBody = isDetails
    ? label(
        'Accurate personal and contact info helps verify withdrawals and keep your account secure.',
        'Accurate personal and contact info helps verify withdrawals and keep your account secure.',
      )
    : isTransactions
      ? label(
          'Filter by Sports or Casino and scroll to load more of your transaction history.',
          'Filter by Sports or Casino and scroll to load more of your transaction history.',
        )
      : isTickets
        ? label(
            'Filter by status and date, then reuse a ticket to load it into your betslip.',
            'Filter by status and date, then reuse a ticket to load it into your betslip.',
          )
        : isClaimBonus
          ? label(
              'Only one lost bonus can be claimed per eligibility window. Clear open tickets and bonus balances before confirming.',
              'Only one lost bonus can be claimed per eligibility window. Clear open tickets and bonus balances before confirming.',
            )
          : isBonuses
            ? label(
                'Complete the wagering requirement before the expiry countdown runs out to unlock your bonus.',
                'Complete the wagering requirement before the expiry countdown runs out to unlock your bonus.',
              )
            : isMessageDetail
              ? label(
                  'Important account and payments notices are delivered here. Unread messages stay marked New until you open them.',
                  'Important account and payments notices are delivered here. Unread messages stay marked New until you open them.',
                )
              : isMessagesList
                ? label(
                    'New messages appear here with an unread marker. Open a message to read the full notice.',
                    'New messages appear here with an unread marker. Open a message to read the full notice.',
                  )
                : isDeposits
                  ? label(
                      'Choose a provider to continue. Limits are shown per method.',
                      'Choose a provider to continue. Limits are shown per method.',
                    )
                  : isWithdrawals
                    ? label(
                        'Payouts go to verified accounts. Processing times vary by method.',
                        'Payouts go to verified accounts. Processing times vary by method.',
                      )
                    : label(
                        'Manage your profile, wallet activity, bonuses, and responsible-gaming controls from this menu.',
                        'Manage your profile, wallet activity, bonuses, and responsible-gaming controls from this menu.',
                      );

  return (
    <div className="flex w-full items-start">
      <AccountSidebar className="sticky top-14 hidden max-h-[calc(100dvh-3.5rem)] lg:flex" />

      <div className="min-w-0 flex-1 px-4 py-4 lg:px-6 lg:py-8">
        <Outlet />
      </div>

      <aside className="sticky top-14 hidden h-[calc(100dvh-3.5rem)] w-80 shrink-0 overflow-hidden px-4 py-6 xl:flex xl:flex-col">
        <AccountTipsCard title={tipTitle} body={tipBody} />
      </aside>
    </div>
  );
}
